import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowsClockwise, Calculator, CheckCircle, CheckSquareOffset, Factory, FilePdf, Package, PaperPlaneTilt, Pencil, Printer, Prohibit, SealCheck, Truck, WarningDiamond, XCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useAuth, isAdmin, isManager } from '@/auth/AuthContext';
import { mktApi, errMsg } from './marketingApi';
import { FULFILLMENT, StatusBadge, WO_STATUS_CLS, jd, jdt, yen } from './mktShared';
import OrderFormDialog from './OrderFormDialog';
import EstimateDialog from './EstimateDialog';
import ApprovalDialog from './ApprovalDialog';
import HandoffDialog from './HandoffDialog';
import KanseiDialog from './KanseiDialog';

const DOC_LABEL = { quotation: '見積書', invoice: '請求書' };

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [docs, setDocs] = useState([]);
  const [settings, setSettings] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [estOpen, setEstOpen] = useState(false);
  const [apvOpen, setApvOpen] = useState(false);
  const [handoffOpen, setHandoffOpen] = useState(false);
  const [kanseiOpen, setKanseiOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [o, d] = await Promise.all([mktApi.order(id), mktApi.documents(id)]);
      setOrder(o); setDocs(d);
    } catch (e) { toast.error(errMsg(e, '受注の取得に失敗しました')); }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    mktApi.settings().then(setSettings).catch(() => {});
    mktApi.customers().then(setCustomers).catch(() => {});
  }, []);

  const run = useCallback(async (fn, okMsg) => {
    setBusy(true);
    try { const o = await fn(); if (o?.id) setOrder(o); if (okMsg) toast.success(okMsg); await load(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  }, [load]);

  const stockMap = useMemo(() => Object.fromEntries(((order?.stock_check?.lines) || []).map((l) => [l.line_id, l])), [order]);
  const canManage = isAdmin(user) || isManager(user);

  if (!order) return <div className="py-16 text-center text-sm text-zinc-400">読み込み中…</div>;

  const s = order.status;
  const est = order.estimate;
  const editable = ['draft', 'quoted'].includes(s);
  const cancellable = ['draft', 'quoted', 'approval_pending', 'approved', 'rejected'].includes(s);

  const issueDoc = (docType) => run(async () => {
    const doc = await mktApi.issueDocument(id, docType);
    window.open(`/marketing/print/${doc.id}`, '_blank');
    return null;
  }, `${DOC_LABEL[docType]}を発行しました`);

  return (
    <div className="space-y-4" data-testid="mkt-order-detail">
      <button type="button" onClick={() => navigate('/marketing/orders')} className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-800 transition-colors" data-testid="mkt-order-back-btn">
        <ArrowLeft size={13} /> 受注一覧へ戻る
      </button>

      {/* Header */}
      <div className="rounded-xl border border-zinc-200 bg-white p-4 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-sm font-bold text-fuchsia-700" data-testid="mkt-order-no">{order.order_no}</span>
              <StatusBadge status={s} label={order.status_label} />
              {order.estimate_stale && <span className="rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[10px] font-medium text-amber-700">見積の再計算が必要</span>}
            </div>
            <h1 className="mt-1 text-lg font-bold text-zinc-900 truncate">{order.title || '（件名なし）'}</h1>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-zinc-500">
              <span>客先: <span className="font-medium text-zinc-800"><span data-no-translate>{order.customer_name}</span></span></span>
              <span>納期: <span className="font-medium text-zinc-800 tabular-nums">{jd(order.due_date)}</span></span>
              <span>担当: {order.sales_rep_name || '—'}</span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] text-zinc-400">合計金額（税込）</div>
            <div className="text-xl font-bold tabular-nums text-zinc-900" data-testid="mkt-order-total">{yen(order.total)}</div>
          </div>
        </div>

        {/* internal-approval banner */}
        {order.needs_internal_approval && ['draft', 'quoted', 'approval_pending'].includes(s) && (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5" data-testid="mkt-internal-approval-banner">
            <div className="flex items-center gap-2 text-xs text-amber-800">
              <WarningDiamond size={16} weight="fill" className="text-amber-500" />
              マージン {est?.margin_pct}% は基準（{settings?.min_margin_pct}%）未満です。見積書の発行には上長承認が必要です。
            </div>
            {canManage && (
              <Button size="sm" disabled={busy} onClick={() => run(() => mktApi.internalApproval(id, {}), '社内承認を記録しました')}
                className="bg-amber-600 hover:bg-amber-700 h-8" data-testid="mkt-internal-approve-btn">
                <SealCheck size={14} className="mr-1" /> 上長として承認する
              </Button>
            )}
          </div>
        )}
        {order.internal_approval && order.internal_approval_valid && (
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] text-emerald-700" data-testid="mkt-internal-approved-chip">
            <SealCheck size={13} weight="fill" /> 社内承認済（{order.internal_approval.by_name}）
          </div>
        )}
        {order.wo_alert && (
          <div className="mt-3 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-xs text-red-700" data-testid="mkt-wo-alert-banner">
            <WarningDiamond size={16} weight="fill" className="text-red-500 shrink-0" />
            中止された製造オーダーがあります。「進捗」タブから該当明細を再依頼するか、受注を中止してください。
          </div>
        )}

        {/* Action bar */}
        <div className="mt-4 flex flex-wrap gap-2 border-t border-zinc-200 pt-3">
          {editable && (
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} data-testid="mkt-order-edit-btn"><Pencil size={14} className="mr-1" /> 編集</Button>
          )}
          {['draft', 'quoted', 'approval_pending'].includes(s) && (
            <>
              <Button variant="outline" size="sm" disabled={busy} onClick={() => run(() => mktApi.stockCheck(id), '在庫を確認しました')} data-testid="mkt-stock-check-btn">
                <Package size={14} className="mr-1" /> 在庫チェック
              </Button>
              <Button variant="outline" size="sm" onClick={() => setEstOpen(true)} data-testid="mkt-estimate-btn">
                <Calculator size={14} className="mr-1" /> 見積計算
              </Button>
              <Button size="sm" disabled={busy || !est} onClick={() => issueDoc('quotation')} className="bg-teal-700 hover:bg-teal-800" data-testid="mkt-issue-quotation-btn">
                <FilePdf size={14} className="mr-1" /> 見積書発行
              </Button>
            </>
          )}
          {s === 'quoted' && (
            <Button variant="outline" size="sm" disabled={busy} onClick={() => run(() => mktApi.present(id), '客先承認待ちにしました')} data-testid="mkt-present-btn">
              <PaperPlaneTilt size={14} className="mr-1" /> 客先へ提示
            </Button>
          )}
          {['quoted', 'approval_pending'].includes(s) && (
            <Button size="sm" onClick={() => setApvOpen(true)} className="bg-teal-700 hover:bg-teal-800" data-testid="mkt-record-approval-btn">
              <CheckCircle size={14} className="mr-1" /> 客先承認を記録
            </Button>
          )}
          {s === 'rejected' && (
            <Button variant="outline" size="sm" disabled={busy} onClick={() => run(() => mktApi.requote(id), '再見積に差し戻しました')} data-testid="mkt-requote-btn">再見積する</Button>
          )}
          {s === 'approved' && (
            <>
              <Button size="sm" onClick={() => setHandoffOpen(true)} className="bg-teal-700 hover:bg-teal-800" data-testid="mkt-send-seizo-btn">
                <Factory size={14} className="mr-1" /> {(order.lines || []).some((l) => l.fulfillment === 'production') ? '製造依頼（Seizoへ）' : '出荷手配へ'}
              </Button>
              <Button size="sm" disabled={busy} onClick={() => issueDoc('invoice')} variant="outline" data-testid="mkt-issue-invoice-btn">
                <FilePdf size={14} className="mr-1" /> 請求書発行
              </Button>
            </>
          )}
          {['sent_to_seizo', 'in_production', 'qc_pending', 'qc_passed', 'ready_to_ship', 'shipped', 'completion_pending', 'completed'].includes(s) && (
            <Button size="sm" disabled={busy} onClick={() => issueDoc('invoice')} variant="outline" data-testid="mkt-issue-invoice-btn2">
              <FilePdf size={14} className="mr-1" /> 請求書発行
            </Button>
          )}
          {s === 'completion_pending' && (
            <Button size="sm" onClick={() => setKanseiOpen(true)} className="bg-teal-700 hover:bg-teal-800" data-testid="mkt-kansei-btn">
              <CheckSquareOffset size={14} className="mr-1" /> 売上確定
            </Button>
          )}
          {s === 'completed' && isAdmin(user) && (
            <Button variant="outline" size="sm" disabled={busy} onClick={() => run(() => mktApi.reopen(id), '売上確定を再開しました')} data-testid="mkt-reopen-btn">
              <ArrowsClockwise size={14} className="mr-1" /> 売上確定を再開
            </Button>
          )}
          {cancellable && (
            <Button variant="ghost" size="sm" onClick={() => setCancelOpen(true)} className="text-red-500 hover:text-red-600 hover:bg-red-50 ml-auto" data-testid="mkt-cancel-btn">
              <Prohibit size={14} className="mr-1" /> 中止
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview">
        <TabsList data-testid="mkt-order-tabs">
          <TabsTrigger value="overview" data-testid="mkt-tab-overview">概要</TabsTrigger>
          {order.handoff && <TabsTrigger value="progress" data-testid="mkt-tab-progress">進捗</TabsTrigger>}
          <TabsTrigger value="documents" data-testid="mkt-tab-documents">書類 {docs.length > 0 && <span className="ml-1 rounded-full bg-zinc-200 px-1.5 text-[10px]">{docs.length}</span>}</TabsTrigger>
          <TabsTrigger value="history" data-testid="mkt-tab-history">履歴</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-200">
              <h3 className="text-sm font-semibold text-zinc-800">明細</h3>
              {order.stock_check && <span className="text-[10px] text-zinc-400">在庫確認: {jdt(order.stock_check.checked_at)}（{order.stock_check.checked_by}）</span>}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200 bg-zinc-50/70 text-left text-[11px] text-zinc-400">
                    <th className="px-4 py-2 font-medium">品名 / 品番</th>
                    <th className="px-3 py-2 font-medium text-right">数量</th>
                    <th className="px-3 py-2 font-medium">区分</th>
                    <th className="px-3 py-2 font-medium">在庫状況</th>
                    <th className="px-3 py-2 font-medium text-right">単価</th>
                    <th className="px-4 py-2 font-medium text-right">金額</th>
                  </tr>
                </thead>
                <tbody>
                  {(order.lines || []).map((ln) => {
                    const sc = stockMap[ln.line_id];
                    const f = FULFILLMENT[ln.fulfillment] || FULFILLMENT.production;
                    return (
                      <tr key={ln.line_id} className="border-b border-zinc-50" data-testid={`mkt-detail-line-${ln.line_id}`}>
                        <td className="px-4 py-2.5">
                          <div className="font-medium text-zinc-800">{ln.name}</div>
                          <div className="flex gap-2">{ln.sku && <span className="font-mono text-[10px] text-zinc-400">{ln.sku}</span>}{ln.notes && <span className="text-[10px] text-zinc-400">{ln.notes}</span>}</div>
                        </td>
                        <td className="px-3 py-2.5 text-right text-xs tabular-nums">{ln.qty} {ln.uom}</td>
                        <td className="px-3 py-2.5"><span className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${f.cls}`}>{f.label}</span></td>
                        <td className="px-3 py-2.5">
                          {sc ? (
                            sc.shortage > 0
                              ? <span className="rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[10px] font-semibold text-amber-700">不足 {sc.shortage}（在庫 {sc.on_hand}）</span>
                              : <span className="rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">在庫OK（{sc.on_hand}）</span>
                          ) : <span className="text-[10px] text-zinc-300">未確認</span>}
                        </td>
                        <td className="px-3 py-2.5 text-right text-xs tabular-nums">{ln.unit_price != null ? yen(ln.unit_price) : '—'}</td>
                        <td className="px-4 py-2.5 text-right text-xs font-semibold tabular-nums">{ln.amount != null ? yen(ln.amount) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-zinc-200 bg-white p-4">
              <h3 className="text-sm font-semibold text-zinc-800 mb-2">見積</h3>
              {est ? (
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between text-zinc-600"><span>マージン率</span><span className="tabular-nums">{est.margin_pct}%</span></div>
                  <div className="flex justify-between text-zinc-600"><span>小計</span><span className="tabular-nums">{yen(est.subtotal)}</span></div>
                  <div className="flex justify-between text-zinc-600"><span>消費税 ({est.tax_rate}%)</span><span className="tabular-nums">{yen(est.tax)}</span></div>
                  <div className="flex justify-between border-t border-zinc-200 pt-1.5 font-bold text-zinc-900"><span>合計</span><span className="tabular-nums">{yen(est.total)}</span></div>
                  <p className="pt-1 text-[10px] text-zinc-400">計算: {jdt(est.calculated_at)}（{est.by}）</p>
                </div>
              ) : <p className="text-xs text-zinc-400">未計算です。「見積計算」から作成してください</p>}
            </div>
            <div className="rounded-xl border border-zinc-200 bg-white p-4">
              <h3 className="text-sm font-semibold text-zinc-800 mb-2">客先承認</h3>
              {order.approval ? (
                <div className="space-y-1 text-xs text-zinc-600">
                  <div>結果: {order.approval.result === 'approved' ? <span className="font-semibold text-emerald-600">承認</span> : <span className="font-semibold text-red-500">却下</span>}</div>
                  <div>確認日: {jd(order.approval.at)} ／ 方法: {order.approval.method}</div>
                  {order.approval.customer_contact && <div>先方担当: {order.approval.customer_contact}</div>}
                  {order.approval.note && <div>備考: {order.approval.note}</div>}
                  {order.approval.evidence_file_id && (
                    <a href={mktApi.fileUrl(order.approval.evidence_file_id)} target="_blank" rel="noreferrer" className="text-fuchsia-600 hover:underline" data-testid="mkt-approval-evidence-link">証憑を表示</a>
                  )}
                </div>
              ) : <p className="text-xs text-zinc-400">未記録です</p>}
              {order.notes && <div className="mt-3 border-t border-zinc-200 pt-2 text-xs text-zinc-500"><span className="text-[10px] text-zinc-400">受注備考</span><br />{order.notes}</div>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="progress" className="space-y-4">
          <div className="rounded-xl border border-zinc-200 bg-white p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-zinc-800">製造進捗</h3>
              {order.handoff && (
                <span className="text-[10px] text-zinc-400">
                  依頼: {jdt(order.handoff.sent_at)}（{order.handoff.sent_by}） / 部門: {order.handoff.department_name || '—'}
                </span>
              )}
            </div>
            <div className="space-y-2">
              {(order.lines || []).map((ln) => {
                const f = FULFILLMENT[ln.fulfillment] || FULFILLMENT.production;
                return (
                  <div key={ln.line_id} className="flex flex-wrap items-center gap-3 rounded-lg border border-zinc-100 bg-zinc-50/50 px-3 py-2.5" data-testid={`mkt-progress-line-${ln.line_id}`}>
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-medium shrink-0 ${f.cls}`}>{f.label}</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-zinc-800 truncate">{ln.name}</div>
                      <div className="text-[10px] text-zinc-400">{ln.qty} {ln.uom}{ln.wo?.manufacturing_no ? ` ・ 製番 ${ln.wo.manufacturing_no}` : ''}</div>
                    </div>
                    {ln.wo ? (
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[11px] text-zinc-500">{ln.wo.wo_no}</span>
                        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${WO_STATUS_CLS[ln.wo.status] || ''}`}>{ln.wo.status_label}</span>
                        {ln.wo.status === 'cancelled' && (
                          <Button size="sm" variant="outline" disabled={busy} className="h-7 text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => run(() => mktApi.rehandoffLine(id, ln.line_id), '明細を再依頼しました')}
                            data-testid={`mkt-rehandoff-${ln.line_id}`}>
                            <ArrowsClockwise size={13} className="mr-1" /> 再依頼
                          </Button>
                        )}
                      </div>
                    ) : (
                      <span className="text-[11px] text-zinc-400">{ln.fulfillment === 'stock' ? '出荷時に在庫引落し' : '—'}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-xl border border-zinc-200 bg-white p-4">
              <h3 className="text-sm font-semibold text-zinc-800 mb-2 flex items-center gap-1.5"><SealCheck size={15} className="text-violet-500" /> 検査（QC）</h3>
              {order.fulfillment_track?.qc ? (
                <div className="space-y-1 text-xs text-zinc-600" data-testid="mkt-progress-qc">
                  <div>結果: {order.fulfillment_track.qc.result === 'pass' ? <span className="font-semibold text-teal-600">合格</span> : <span className="font-semibold text-red-500">不合格</span>}</div>
                  <div>{jdt(order.fulfillment_track.qc.at)} ／ {order.fulfillment_track.qc.by}</div>
                  {order.fulfillment_track.qc.note && <div>備考: {order.fulfillment_track.qc.note}</div>}
                </div>
              ) : <p className="text-xs text-zinc-400">未実施（Seizoの出荷管理で記録）</p>}
            </div>
            <div className="rounded-xl border border-zinc-200 bg-white p-4">
              <h3 className="text-sm font-semibold text-zinc-800 mb-2 flex items-center gap-1.5"><Truck size={15} className="text-emerald-500" /> 出荷</h3>
              {(() => {
                const ships = order.fulfillment_track?.shipments
                  || (order.fulfillment_track?.shipment ? [order.fulfillment_track.shipment] : []);
                if (!ships.length) {
                  return order.fulfillment_track?.ready
                    ? <p className="text-xs text-cyan-600">出荷準備完了（{jdt(order.fulfillment_track.ready.at)}）</p>
                    : <p className="text-xs text-zinc-400">未出荷</p>;
                }
                return (
                  <div className="space-y-2" data-testid="mkt-progress-shipment">
                    {order.status === 'partially_shipped' && (
                      <p className="rounded bg-amber-50 border border-amber-100 px-2 py-1 text-[11px] text-amber-700">一部出荷（残数あり）</p>
                    )}
                    {ships.map((sh, i) => (
                      <div key={i} className="space-y-0.5 text-xs text-zinc-600 border-b border-zinc-50 pb-1.5 last:border-0 last:pb-0" data-testid={`mkt-shipment-${i}`}>
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold">{jd(sh.shipped_at)}</span>
                          {sh.carrier && <span className="text-zinc-400">{sh.carrier}</span>}
                          {sh.tracking_no && <span className="font-mono text-[10px] text-zinc-400">{sh.tracking_no}</span>}
                        </div>
                        {(sh.lines || []).length > 0 && (
                          <div className="text-[11px] text-zinc-500">{sh.lines.map((l) => `${l.name}×${l.qty}`).join('、')}</div>
                        )}
                        <div className="text-[10px] text-zinc-400">担当: {sh.by}</div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
            <div className="rounded-xl border border-zinc-200 bg-white p-4">
              <h3 className="text-sm font-semibold text-zinc-800 mb-2 flex items-center gap-1.5"><CheckSquareOffset size={15} className="text-fuchsia-500" /> 売上確定</h3>
              {order.kansei ? (
                <div className="space-y-1 text-xs text-zinc-600" data-testid="mkt-progress-kansei">
                  <div>売上確定日: {jd(order.kansei.completed_at)}</div>
                  {order.kansei.final_amount != null && <div>確定売上: <span className="font-semibold tabular-nums">{yen(order.kansei.final_amount)}</span>
                    {order.total != null && order.kansei.final_amount !== order.total && <span className="ml-1 text-[10px] text-amber-600">（見積 {yen(order.total)}）</span>}</div>}
                  {order.kansei.technoa_no && <div>Technoa: <span className="font-mono">{order.kansei.technoa_no}</span></div>}
                  <div>入力者: {order.kansei.by}</div>
                  {order.kansei.note && <div>備考: {order.kansei.note}</div>}
                </div>
              ) : s === 'completion_pending' ? (
                <p className="text-xs text-fuchsia-600">売上確定待ちです。「売上確定」ボタンから記録してください</p>
              ) : <p className="text-xs text-zinc-400">未実施</p>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="documents">
          <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
            {docs.length === 0 ? (
              <p className="px-4 py-10 text-center text-sm text-zinc-400" data-testid="mkt-docs-empty">発行済みの書類はありません</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200 bg-zinc-50/70 text-left text-[11px] text-zinc-400">
                    <th className="px-4 py-2 font-medium">書類番号</th>
                    <th className="px-3 py-2 font-medium">種類</th>
                    <th className="px-3 py-2 font-medium">発行日時</th>
                    <th className="px-3 py-2 font-medium">発行者</th>
                    <th className="px-3 py-2 font-medium text-right">金額</th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {docs.map((d) => (
                    <tr key={d.id} className="border-b border-zinc-50" data-testid={`mkt-doc-row-${d.id}`}>
                      <td className="px-4 py-2.5 font-mono text-xs font-semibold text-zinc-700">{d.doc_no}{d.stale && <span className="ml-2 rounded bg-amber-50 px-1.5 text-[10px] text-amber-600">要再発行</span>}</td>
                      <td className="px-3 py-2.5 text-xs">{DOC_LABEL[d.doc_type]}</td>
                      <td className="px-3 py-2.5 text-xs tabular-nums">{jdt(d.issued_at)}</td>
                      <td className="px-3 py-2.5 text-xs">{d.issued_by}</td>
                      <td className="px-3 py-2.5 text-right text-xs tabular-nums">{yen(d.snapshot?.total)}</td>
                      <td className="px-4 py-2.5 text-right">
                        <Button variant="outline" size="sm" className="h-7" onClick={() => window.open(`/marketing/print/${d.id}`, '_blank')} data-testid={`mkt-doc-print-${d.id}`}>
                          <Printer size={13} className="mr-1" /> 印刷
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="history">
          <div className="rounded-xl border border-zinc-200 bg-white p-4">
            <ol className="relative space-y-4 border-l border-zinc-200 pl-5 ml-1">
              {[...(order.status_history || [])].reverse().map((h, i) => (
                <li key={i} className="relative" data-testid={`mkt-history-${i}`}>
                  <span className={`absolute -left-[26px] top-1 h-2.5 w-2.5 rounded-full ${i === 0 ? 'bg-fuchsia-500 ring-4 ring-fuchsia-100' : 'bg-zinc-300'}`} />
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    {h.from !== h.to && h.from && <StatusBadge status={h.from} className="opacity-60" />}
                    {h.from !== h.to && h.from && <span className="text-zinc-300">→</span>}
                    <StatusBadge status={h.to} />
                    <span className="text-zinc-400">{jdt(h.at)}</span>
                    <span className="text-zinc-500">{h.by_name}</span>
                  </div>
                  {h.note && <p className="mt-0.5 text-xs text-zinc-600">{h.note}</p>}
                </li>
              ))}
            </ol>
          </div>
        </TabsContent>
      </Tabs>

      <OrderFormDialog open={editOpen} onOpenChange={setEditOpen} order={order} customers={customers}
        onSaved={() => { setEditOpen(false); load(); }} />
      <EstimateDialog open={estOpen} onOpenChange={setEstOpen} order={order} settings={settings}
        onSaved={(o) => setOrder(o)} />
      <ApprovalDialog open={apvOpen} onOpenChange={setApvOpen} order={order}
        onSaved={(o) => setOrder(o)} />
      <HandoffDialog open={handoffOpen} onOpenChange={setHandoffOpen} order={order}
        onSaved={() => load()} />
      <KanseiDialog open={kanseiOpen} onOpenChange={setKanseiOpen} order={order}
        onSaved={(o) => setOrder(o)} />

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="max-w-sm" data-testid="mkt-cancel-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><XCircle size={18} className="text-red-500" /> 受注を中止しますか？</DialogTitle></DialogHeader>
          <Textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="中止理由（任意）" rows={2} data-testid="mkt-cancel-reason" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setCancelOpen(false)} data-testid="mkt-cancel-abort-btn">戻る</Button>
            <Button disabled={busy} onClick={() => { setCancelOpen(false); run(() => mktApi.cancel(id, cancelReason || null), '受注を中止しました'); }}
              className="bg-red-600 hover:bg-red-700" data-testid="mkt-cancel-confirm-btn">中止する</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
